import { format } from 'date-fns';
import { CreateDialogFieldCompanion } from 'components/create-dialog/CreateDialogRequired';
import EmailListFormItem from 'components/forms/inputs/EmailListInput';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput.tsx';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { TimeInput } from 'components/forms/inputs/rhf-inputs/TimeInput';
import InfoTooltip from 'components/InfoTooltip';
import { SectionFormEnd, useInRecordSection } from 'components/section-edit/SectionEdit';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { t } from 'utils/getTranslations';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate.ts';
import { TaskFormState } from './useTaskForm';

/** Deadline & calendar sync: the deadline and the switch for the calendar event; {@link TaskEventFields} follow it. */
export const DeadlineCalendarFields = ({ form }: { form: TaskFormState }): JSX.Element => {
    const { control } = form.methods;
    const { hasDeadline, handleHasDeadlineToggle, type, calendarSync } = form;

    return (
        <>
            <CheckboxInput
                control={control}
                name="hasDeadline"
                label="tasks.form.has-deadline"
                onChange={handleHasDeadlineToggle}
            />
            {hasDeadline ? (
                <DateInput fullWidth control={control} name="deadlineDate" label="tasks.form.deadline-date" required />
            ) : null}

            <CheckboxInput
                control={control}
                name="calendarSync"
                label="tasks.form.calendar-sync"
                disabled={type === 'edit' && calendarSync}
            />
        </>
    );
};

/** The calendar event, shown while calendar sync is on. */
export const TaskEventFields = ({ form }: { form: TaskFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        eventDateValidationError,
        eventTimeValidationError,
        allDayMeeting,
        handleAllDayMeetingToggle,
        removeEmail,
        addEmail,
        replyTo,
        reminderOptions,
        type,
        initialData,
        handleCancelCalendarInvite,
    } = form;
    // On the record page each date and each time is a cell of the card's grid, where the read view has its row;
    // the reply-to addresses and the cancel link, which no read row shows, follow the fields there.
    const inSection = useInRecordSection();
    const pairClass = inSection ? 'contents' : 'flex justify-between gap-[20px]';

    return (
        <>
            {/* In the create dialog the event's dates are drawn in Required, and what is wrong with them goes along. */}
            <CreateDialogFieldCompanion field="taskEvent.dateStart">
                {eventDateValidationError ? (
                    <p style={{ color: 'var(--danger-ink, red)' }}>{t('tasks.form.date-error')}</p>
                ) : null}
                {eventTimeValidationError ? (
                    <p style={{ color: 'var(--danger-ink, red)' }}>{t('tasks.form.time-error')}</p>
                ) : null}
            </CreateDialogFieldCompanion>

            <div className={pairClass}>
                <DateInput
                    fullWidth
                    control={control}
                    name="taskEvent.dateStart"
                    label="tasks.form.date-start"
                    required={true}
                    hasErrors={eventDateValidationError}
                    onChange={(value) => {
                        if (allDayMeeting && value) {
                            setValue('taskEvent.dateEnd', format(value, DEFAULT_DATE_FORMAT));
                        }
                    }}
                />
                <TimeInput
                    control={control}
                    name="taskEvent.timeStart"
                    label={'tasks.form.time-start'}
                    setValue={setValue}
                    required={!allDayMeeting}
                    disabled={allDayMeeting}
                    timeHasErrors={eventTimeValidationError}
                />
            </div>
            <div className={pairClass}>
                <DateInput
                    fullWidth
                    control={control}
                    name="taskEvent.dateEnd"
                    label="tasks.form.date-end"
                    required={true}
                    disabled={allDayMeeting}
                    hasErrors={eventDateValidationError}
                />
                <TimeInput
                    control={control}
                    name="taskEvent.timeEnd"
                    label={'tasks.form.time-end'}
                    setValue={setValue}
                    required={!allDayMeeting}
                    disabled={allDayMeeting}
                    timeHasErrors={eventTimeValidationError}
                />
            </div>
            {/* It lifts the two times' requirement, so in the create dialog it stays beside them. */}
            <CreateDialogFieldCompanion field="taskEvent.timeStart">
                <CheckboxInput
                    control={control}
                    name="taskEvent.allDayMeeting"
                    label="tasks.form.all-day-meeting"
                    onChange={handleAllDayMeetingToggle}
                />
            </CreateDialogFieldCompanion>
            <TextInput
                name="taskEvent.inviteSender"
                control={control}
                label="tasks.form.meeting-organiser"
                placeholder="tasks.form.meeting-organiser"
                required
                readonly={true}
            />
            <SectionFormEnd>
                <EmailListFormItem
                    control={control}
                    name="replyTo"
                    onRemove={(val) => removeEmail(val, 'replyTo')}
                    addEmail={(val) => addEmail(val, 'replyTo')}
                    label="invoices.email.reply-to"
                    // Value needs to me mapped, wasnt able to bend the controller value type.
                    watchedValue={replyTo || []}
                />
            </SectionFormEnd>
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="tasks.form.invite-colleagues"
                placeholder="tasks.form.invite-colleagues"
                name="taskEvent.eventInvitees"
                paginated={{
                    fetch: useLazyGetEmployeesListWithFiltersQuery,
                    searchField: 'fullName',
                }}
                getOptionLabel="name"
                getOptionIdentifier="id"
                required={type === 'add'}
                tooltip={<InfoTooltip text={'tasks.form.invite-colleagues-tooltip'} />}
            />
            <TextInput
                name="taskEvent.eventLocation"
                control={control}
                label="tasks.form.location"
                placeholder="tasks.form.location"
            />
            <TextAreaInput
                control={control}
                label="tasks.form.event-description"
                placeholder="tasks.form.event-description"
                name="taskEvent.description"
            />
            <SelectInput
                setValue={setValue}
                control={control}
                label="tasks.form.event-reminder"
                placeholder="tasks.form.event-reminder"
                name="taskEvent.reminder"
                options={reminderOptions}
                getOptionLabel="label"
                getOptionIdentifier="value"
            />
            {type === 'edit' && initialData?.taskEvent && (
                <SectionFormEnd>
                    <div>
                        <button
                            type="button"
                            onClick={handleCancelCalendarInvite}
                            className="text-body1 text-fg-error cursor-pointer font-normal no-underline hover:underline"
                        >
                            {t('tasks.form.cancel-calendar-invite')}
                        </button>
                    </div>
                </SectionFormEnd>
            )}
        </>
    );
};
